// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import axios from 'axios';

import * as constants from '../../constants';
import { getConfig } from '../../reducers/portalConfig';
import { getValidationErrors } from '../../utils';

const init = (dispatch, isLoading = true) =>
  dispatch({
    type: constants.APP_RESET,
    isLoading,
  });

export const fetchDocumentTree = ({ entityUuid, entityType, locale }) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/document-service/1.0/docs/${entityType}/${entityUuid}?locale=${locale}`;
    dispatch({ type : constants.ENTITY_GET_DOCUMENT_LIST_FETCH });
    await axios.get(url, { credentials: 'include' })
    .then(response => {
      const itemData = response.data.map(item => ({
        ...item,
        id: buildDocumentId(
          entityType,
          entityUuid,
          item.navtitle,
          locale,
        ),
      }));
      const data = { itemData, total: itemData.length };
      dispatch({
        type: constants.ENTITY_GET_DOCUMENT_LIST_SUCCESS,
        payload: data,
      });
    })
    .catch(error =>
      dispatch({
        type: constants.ENTITY_GET_DOCUMENT_LIST_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };

export const addNewDocument = (document) =>
  async (dispatch) => {
    dispatch({
      type: constants.ENTITY_ADD_DOCUMENT,
      payload: document,
    });
  };

export const removeNewDocument = () =>
  async (dispatch) => {
    dispatch({
      type: constants.ENTITY_ADD_DOCUMENT,
      payload: null,
    });
  };

export const fetchDocument = ({ entityUuid, entityType, locale, navtitle }) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/document-service/1.0/docs/${entityType}/${entityUuid}/${encodeURIComponent(
      navtitle)}?locale=${locale}`;
    await axios.get(url, { credentials: 'include' })
    .then(response => {
      const data = {
        ...response.data,
        id: buildDocumentId(
          entityType,
          entityUuid,
          response.data.navtitle,
          locale,
        ),
      };
      dispatch({
        type: constants.ENTITY_GET_DOCUMENT_SUCCESS,
        payload: data,
      });
    })
    .catch(error =>
      dispatch({
        type: constants.ENTITY_GET_DOCUMENT_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };

export const saveDocument = (doc, entityUuid, entityType) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/document-service/1.0/docs/${entityType}/${entityUuid}/${encodeURIComponent(
      doc.navtitle)}`;
    await axios.post(url, doc, { credentials: 'include' })
    .then(() => {
      dispatch({
        type: constants.ENTITY_DOCUMENT_SAVE_SUCCESS,
        payload: doc,
      });
    })
    .catch((error) =>
      dispatch({
        type: constants.ENTITY_DOCUMENT_SAVE_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };

export const deleteDocument = (doc, entityUuid, entityType) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;
    const { navtitle, status, locale } = doc;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/document-service/1.0/docs/${entityType}/${entityUuid}/${encodeURIComponent(
      navtitle)}?forceDelete=true&status=${status}&locale=${locale}`;
    await axios.delete(url, { credentials: 'include' })
    .then((response) => {
      dispatch({
        type: constants.ENTITY_DOCUMENT_DELETE_SUCCESS,
        payload: response.data,
      });
    })
    .catch((error) =>
      dispatch({
        type: constants.ENTITY_DOCUMENT_DELETE_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };

export const updateDocumentTree = (entityUuid, entityType, locale, data) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/document-service/1.0/docs/${entityType}/${entityUuid}?locale=${locale}`;
    await axios.put(url, data, { credentials: 'include' })
    .then((response) => {
      dispatch({
        type: constants.ENTITY_DOCUMENT_UPDATE_TREE_SUCCESS,
        payload: response.data,
      });
    })
    .catch((error) =>
      dispatch({
        type: constants.ENTITY_DOCUMENT_UPDATE_TREE_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };

export const resetDocumentNotification = () => dispatch =>
  dispatch({ type: constants.ENTITY_DOCUMENT_RESET_NOTIFICATION });

export const buildDocumentId = (entityType, entityUuid, navtitle, locale) => {
  return `${entityType}/${entityUuid}/${navtitle}/${locale}`;
};

